import * as Sentry from "@sentry/react";
import { identifyMixpanelUser, trackMixpanelEvent } from "./mixpanel";

const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN;

const SENSITIVE_KEY_PATTERN =
  /pass(word)?|pwd|secret|token|authorization|auth|bearer|cvc|cvv|security.?code|card.?number|card\[|access.?token|refresh.?token|api.?key|stripe|sk_live|sk_test|pk_live|session/i;

const MAX_STRING = 2000;
const MAX_DEPTH = 6;

const isPlainObject = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);

export const sanitizeForSentry = (value, depth = 0) => {
  if (value === null || value === undefined) return value;
  if (depth > MAX_DEPTH) return "[MaxDepth]";

  if (typeof value === "string") {
    return value.length > MAX_STRING
      ? `${value.slice(0, MAX_STRING)}…[truncated]`
      : value;
  }

  if (typeof value === "number" || typeof value === "boolean") return value;

  if (value instanceof Error) {
    return {
      name: value.name,
      message: value.message,
      stack: value.stack,
    };
  }

  if (Array.isArray(value)) {
    return value.slice(0, 50).map((item) => sanitizeForSentry(item, depth + 1));
  }

  if (isPlainObject(value)) {
    const out = {};
    Object.keys(value).forEach((key) => {
      if (SENSITIVE_KEY_PATTERN.test(key)) {
        out[key] = "[REDACTED]";
        return;
      }
      try {
        out[key] = sanitizeForSentry(value[key], depth + 1);
      } catch (e) {
        out[key] = "[Unserializable]";
      }
    });
    return out;
  }

  try {
    return String(value).slice(0, MAX_STRING);
  } catch (e) {
    return "[Unserializable]";
  }
};

const getBrowserContext = () => {
  if (typeof window === "undefined") return {};
  return {
    href: window.location?.href,
    pathname: window.location?.pathname,
    search: window.location?.search,
    hash: window.location?.hash,
    referrer: document.referrer || undefined,
    userAgent: navigator.userAgent,
    language: navigator.language,
    online: navigator.onLine,
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight,
    },
    screen: {
      width: window.screen?.width,
      height: window.screen?.height,
    },
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
};

export const initSentry = () => {
  if (!SENTRY_DSN) {
    if (process.env.NODE_ENV === "development") {
      // eslint-disable-next-line no-console
      console.info(
        "[Sentry] REACT_APP_SENTRY_DSN is not set — error logging disabled."
      );
    }
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: process.env.REACT_APP_SENTRY_ENV || process.env.NODE_ENV,
    release:
      process.env.REACT_APP_SENTRY_RELEASE ||
      `gotheextramile@${process.env.npm_package_version || "0.1.0"}`,
    sendDefaultPii: false,
    attachStacktrace: true,
    normalizeDepth: 8,
    maxBreadcrumbs: 80,
    tracesSampleRate: Number(
      process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE || 0.35
    ),
    replaysSessionSampleRate: Number(
      process.env.REACT_APP_SENTRY_REPLAY_SESSION_RATE || 0.05
    ),
    replaysOnErrorSampleRate: Number(
      process.env.REACT_APP_SENTRY_REPLAY_ERROR_RATE || 1.0
    ),
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        maskAllInputs: true,
        blockAllMedia: false,
      }),
    ],
    ignoreErrors: [
      "ResizeObserver loop limit exceeded",
      "ResizeObserver loop completed with undelivered notifications",
      /^Loading chunk .* failed/,
    ],
    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb?.data) {
        breadcrumb.data = sanitizeForSentry(breadcrumb.data);
      }
      return breadcrumb;
    },
    beforeSend(event, hint) {
      if (event.extra) {
        event.extra = sanitizeForSentry(event.extra);
      }
      if (event.contexts) {
        event.contexts = sanitizeForSentry(event.contexts);
      }
      if (event.request?.data) {
        event.request.data = sanitizeForSentry(event.request.data);
      }

      event.contexts = {
        ...event.contexts,
        app_runtime: sanitizeForSentry({
          ...getBrowserContext(),
          apiBaseURL: process.env.REACT_APP_API_URL,
          appBaseURL: process.env.REACT_APP_URL,
          platform: "web",
          capturedAt: new Date().toISOString(),
        }),
      };

      const original = hint?.originalException;
      if (original?.isAxiosError) {
        event.tags = {
          ...event.tags,
          axios: "true",
          http_status: String(original?.response?.status || "network"),
        };
      }

      return event;
    },
  });

  Sentry.setTag("app", "gotheextramile-react-webapp");
  Sentry.setTag("platform", "web");
  Sentry.setContext("build", {
    apiBaseURL: process.env.REACT_APP_API_URL,
    appBaseURL: process.env.REACT_APP_URL,
    nodeEnv: process.env.NODE_ENV,
  });

  window.addEventListener("unhandledrejection", (event) => {
    captureException(event.reason || new Error("Unhandled rejection"), {
      source: "window.unhandledrejection",
      ...getBrowserContext(),
    });
  });

  window.addEventListener("error", (event) => {
    if (event.error) {
      captureException(event.error, {
        source: "window.error",
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        ...getBrowserContext(),
      });
    }
  });
};

export const setSentryUser = (user) => {
  identifyMixpanelUser(user || null);

  if (!SENTRY_DSN) return;
  if (!user) {
    Sentry.setUser(null);
    Sentry.setTag("authenticated", "false");
    return;
  }
  Sentry.setUser({
    id: user?._id || user?.userId || undefined,
    email: user?.email || undefined,
    username: user?.name || undefined,
  });
  Sentry.setTag("authenticated", "true");
  Sentry.setContext(
    "user_profile",
    sanitizeForSentry({
      id: user?._id || user?.userId,
      email: user?.email,
      name: user?.name,
      firstName: user?.firstName,
      lastName: user?.lastName,
      verifyAcc: user?.verifyAcc,
    })
  );
};

export const setSentryTags = (tags = {}) => {
  if (!SENTRY_DSN) return;
  Object.entries(tags).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      Sentry.setTag(key, String(value));
    }
  });
};

export const captureException = (error, context = {}) => {
  if (!SENTRY_DSN) return;

  const safeContext = sanitizeForSentry({
    ...getBrowserContext(),
    ...context,
  });

  Sentry.withScope((scope) => {
    if (context.level) scope.setLevel(context.level);
    if (context.tags) {
      Object.entries(context.tags).forEach(([k, v]) =>
        scope.setTag(k, String(v))
      );
    }
    if (context.fingerprint) scope.setFingerprint(context.fingerprint);

    Object.entries(safeContext).forEach(([key, value]) => {
      if (key === "level" || key === "tags" || key === "fingerprint") return;
      scope.setExtra(key, value);
    });

    const err =
      error instanceof Error
        ? error
        : new Error(
            typeof error === "string"
              ? error
              : error?.message || JSON.stringify(sanitizeForSentry(error))
          );

    if (!(error instanceof Error) && error && typeof error === "object") {
      scope.setExtra("originalError", sanitizeForSentry(error));
    }

    Sentry.captureException(err);
  });
};

export const captureMessage = (message, level = "info", context = {}) => {
  if (!SENTRY_DSN) return;
  const safeContext = sanitizeForSentry({
    ...getBrowserContext(),
    ...context,
  });

  Sentry.withScope((scope) => {
    scope.setLevel(level);
    if (context.tags) {
      Object.entries(context.tags).forEach(([k, v]) =>
        scope.setTag(k, String(v))
      );
    }
    Object.entries(safeContext).forEach(([key, value]) => {
      if (key === "tags") return;
      scope.setExtra(key, value);
    });
    Sentry.captureMessage(message, level);
  });
};

export const addBreadcrumb = (breadcrumb) => {
  if (!SENTRY_DSN) return;
  Sentry.addBreadcrumb({
    ...breadcrumb,
    data: breadcrumb?.data
      ? sanitizeForSentry(breadcrumb.data)
      : undefined,
    timestamp: breadcrumb?.timestamp || Date.now() / 1000,
  });
};

export const trackActivity = (action, data = {}, options = {}) => {
  const safeData = sanitizeForSentry(data);
  const level = options.level || "info";

  trackMixpanelEvent(action, safeData);

  addBreadcrumb({
    category: options.category || "user.activity",
    message: action,
    level,
    data: safeData,
  });

  if (level === "warning" || level === "error") {
    captureMessage(`activity:${action}`, level, {
      ...safeData,
      tags: {
        activity: action,
        ...(options.tags || {}),
      },
    });
  }
};

export const trackNavigation = (location) => {
  if (!SENTRY_DSN) return;
  const path =
    typeof location === "string"
      ? location
      : `${location?.pathname || ""}${location?.search || ""}${location?.hash || ""}`;

  addBreadcrumb({
    category: "navigation",
    type: "navigation",
    level: "info",
    message: path,
    data: sanitizeForSentry({
      pathname: location?.pathname,
      search: location?.search,
      hash: location?.hash,
    }),
  });

  Sentry.setTag("route", location?.pathname || path);
  Sentry.setContext(
    "page",
    sanitizeForSentry({
      path,
      ...getBrowserContext(),
    })
  );
};

export const logApiFailure = (error, meta = {}) => {
  const status = error?.response?.status;
  const url = error?.config?.url;
  const method = error?.config?.method;
  const responseData = sanitizeForSentry(error?.response?.data);
  const requestData = sanitizeForSentry(error?.config?.data);

  captureException(error, {
    source: meta.source || "api",
    tags: {
      feature: meta.feature || "api",
      http_status: String(status || "network"),
      ...(meta.tags || {}),
    },
    method,
    url,
    baseURL: error?.config?.baseURL,
    status,
    statusText: error?.response?.statusText,
    code: error?.code,
    responseData,
    requestData,
    headers: sanitizeForSentry({
      "content-type": error?.response?.headers?.["content-type"],
      "platform-type": error?.config?.headers?.["Platform-Type"],
    }),
    durationMs: meta.durationMs,
    ...meta.extra,
  });
};

export const sendSentrySmokeTest = async () => {
  if (!SENTRY_DSN) {
    return { ok: false, reason: "no_dsn" };
  }

  const runId = `smoke_${Date.now()}`;

  setSentryTags({
    smoke_test: "true",
    smoke_run_id: runId,
  });

  setSentryUser({
    _id: "smoke-test-user",
    email: "sentry-smoke@example.com",
    name: "Sentry Smoke Test",
  });

  trackNavigation({
    pathname: "/sentry-smoke-test",
    search: `?run=${runId}`,
    hash: "",
  });

  addBreadcrumb({
    category: "smoke",
    level: "info",
    message: "smoke.breadcrumb",
    data: { runId },
  });

  trackActivity("smoke.activity", {
    runId,
    feature: "smoke_test",
    cartId: "cart_smoke_001",
    amount: "9.99",
    password: "should-be-redacted",
    card_number: "4242424242424242",
    securitycode: "123",
    Authorization: "Bearer secret-token",
  });

  captureMessage("smoke.message.info", "info", {
    runId,
    tags: { smoke: "message" },
  });

  captureMessage("smoke.message.warning", "warning", {
    runId,
    tags: { smoke: "message" },
  });

  const fakeAxiosError = new Error("Smoke test: simulated API failure");
  fakeAxiosError.isAxiosError = true;
  fakeAxiosError.code = "ERR_BAD_RESPONSE";
  fakeAxiosError.config = {
    method: "post",
    url: "/v1/smoke-test",
    baseURL: process.env.REACT_APP_API_URL,
    data: JSON.stringify({
      email: "sentry-smoke@example.com",
      password: "should-be-redacted",
      card_number: "4242424242424242",
    }),
    headers: { "Platform-Type": "web" },
  };
  fakeAxiosError.response = {
    status: 500,
    statusText: "Internal Server Error",
    headers: { "content-type": "application/json" },
    data: {
      value: false,
      error: { message: "Simulated server error for Sentry smoke test" },
    },
  };

  logApiFailure(fakeAxiosError, {
    source: "smoke.api",
    feature: "smoke_test",
    durationMs: 42,
    tags: { smoke: "api" },
  });

  captureException(new Error("Smoke test: captureException"), {
    source: "smoke.exception",
    tags: { feature: "smoke_test", smoke: "exception" },
    runId,
    password: "should-be-redacted",
    token: "should-be-redacted",
  });

  try {
    await Sentry.flush(5000);
  } catch (e) {}

  return { ok: true, runId };
};

export { Sentry };
